import { useEffect } from 'react';

import { useMutation } from '@apollo/client';

import { useEvent } from 'common/components/hooks/useEvent/useEvent';
import { getTokenExchangeTimeout, getTokenFromLocalStorage, setTokenToLocalStorage } from 'common/helpers/jwtHelper';
import useLocalStorageBackedState from 'common/helpers/useLocalStorageBackedState';
import usePrevious from 'common/helpers/usePrevious';
import { TrackedError } from 'ErrorBoundary/Error';

import { ExchangeTokenMutation } from './mutations';
import tokenWatcherSubject from './tokenWatcherSubject';

/**
 * A hook that handles the token exchange and persistence.
 */
export default function useToken() {
  const [token, setToken] = useLocalStorageBackedState({
    get: getTokenFromLocalStorage,
    set: setTokenToLocalStorage,
  });

  const [exchangeTokenMutation] = useMutation(ExchangeTokenMutation);

  const tokenExchangeHandler = useEvent(async () => {
    const { data } = await exchangeTokenMutation();
    const newToken = data?.exchangeToken?.token ?? null;

    if (!newToken || typeof newToken !== 'string') {
      throw new TrackedError({
        title: 'Invalid token received',
        content: `Token update failed on schedule token exchange because of invalid token value: ${newToken}`,
      });
    }

    setToken(newToken);
  });

  // Schedules the exchange of the token some time before it expires, and on success writes the new token
  // to local storage. Schedules a new exchange when the token changes.
  useEffect(() => {
    const tokenExchangeTimerId = window.setTimeout(tokenExchangeHandler, getTokenExchangeTimeout(token!));

    return () => {
      window.clearTimeout(tokenExchangeTimerId);
    };
  }, [token, tokenExchangeHandler]);

  const previousToken = usePrevious(token);
  useEffect(() => {
    if (token !== previousToken && token) {
      tokenWatcherSubject.next(token);
    }
  }, [token, previousToken]);
  return { token: token! };
}
